
import React, { useEffect } from 'react';
import { ArrowRight, Check, Plug, Clock, Cloud, Code, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageLayout from '@/components/layout/PageLayout';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Card, CardContent } from '@/components/ui/card';

// Extend Window interface to include neetoCal
declare global {
  interface Window {
    neetoCal?: {
      embed: (config: {
        type: string;
        id: string;
        organization: string;
        elementSelector: string;
        styles: { height: string; width: string };
        isSidebarAndCoverImgHidden: string;
      }) => void;
    };
  }
}

const FacebookAdLanding = () => {
  // Initialize NeetoCal when the component mounts
  useEffect(() => {
    // Create and load the NeetoCal script
    const script = document.createElement('script');
    script.src = "https://cdn.neetocal.com/javascript/embed.js";
    script.async = true;
    document.head.appendChild(script);
    
    script.onload = function() {
      // Check if window.neetoCal is available
      if (window.neetoCal) {
        // Desktop embed
        window.neetoCal.embed({
          type: "inline",
          id: "fc21b2d4-82d5-4910-a7af-77217e83439f",
          organization: "astrem",
          elementSelector: "#inline-embed-container",
          styles: { height: "100%", width: "100%"},
          isSidebarAndCoverImgHidden: "true"
        });
        
        // Mobile embed
        window.neetoCal.embed({
          type: "inline",
          id: "fc21b2d4-82d5-4910-a7af-77217e83439f",
          organization: "astrem",
          elementSelector: "#inline-embed-container-mobile",
          styles: { height: "100%", width: "100%"},
          isSidebarAndCoverImgHidden: "true"
        });
      }
    };
    
    // Clean up
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <PageLayout
      title="Ditch WordPress & Get a Custom Website | Astrem Website Design"
      description="Astrem designs fast, visually striking websites for companies ready to leave WordPress headaches behind. No plugins, no security issues - just beautiful custom websites."
      ogImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/astrem_og_Websites%20Content%20Marketing%20Solutions.jpg"
      canonicalPath="/facebook-ad-landing"
      className="bg-white"
      navStyle="light"
    >
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-white to-astrem-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            {/* Left Side - Text Content */}
            <div className="lg:w-1/2">
              <ScrollReveal>
                <span className="inline-block px-4 py-1.5 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-6 mt-[60px] lg:mt-0">
                  Custom Website Design for Established Businesses
                </span>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-astrem-black leading-tight mb-4">
                  Ditch the Plugins. Get a Beautiful, Custom Website Built for Your Business.
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <h2 className="text-xl md:text-2xl text-astrem-gray-700 mb-8">
                  Astrem designs fast, visually striking websites for companies ready to leave WordPress headaches behind.
                </h2>
              </ScrollReveal>

              <ScrollReveal delay={300}>
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-astrem-black mb-4">Why Business Owners Switch to Astrem</h3>
                  <ul className="space-y-3">
                    {[
                      {icon: MessageSquare, text: "Transparent proposal process (book a free discovery call)"},
                      {icon: Code, text: "Human-written copy, no AI"},
                      {icon: Check, text: "Pixel-perfect designs for your brand"},
                      {icon: Plug, text: "Built on Framer—no plugins or security issues"},
                      {icon: RefreshCw, text: "Fast turnaround. Clear communication. Easy updates."},
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <item.icon className="w-5 h-5 text-astrem-black mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-astrem-gray-800">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>

              <div className="lg:hidden">
                <ScrollReveal delay={400}>
                  <p className="font-medium mb-4 text-center text-lg">Book Your Free Discovery Call</p>
                </ScrollReveal>
                {/* NeetoCal Embed Form for Mobile */}
                <div style={{ height: "717px", width: "100%", position: "relative", zIndex: 10 }} id="inline-embed-container-mobile">
                  {/* NeetoCal widget will be added here */}
                </div>
              </div>
            </div>

            {/* Right Side - Booking Form (Desktop Only) */}
            <div className="hidden lg:block lg:w-1/2">
              <Card className="border border-astrem-gray-200 shadow-lg rounded-xl p-1">
                <CardContent className="p-0">
                  {/* NeetoCal Embed Form */}
                  <div style={{ height: "717px", width: "100%", position: "relative", zIndex: 10 }} id="inline-embed-container">
                    {/* NeetoCal widget will be added here */}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Built with Process, Not Guesswork */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-astrem-black mb-6 text-center">
              Built with Process, Not Guesswork
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-700 max-w-3xl mx-auto text-center mb-12">
              We're not just designers. We're builders with a battle-tested process that removes confusion and delays. 
              After your discovery call, we guide you through our 15-step fulfillment—from creative brief and design 
              to development, testing, and launch.
            </p>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Process Timeline Line */}
              <div className="absolute top-0 bottom-0 left-8 w-0.5 bg-astrem-gray-200 hidden md:block"></div>
              
              {/* Process Steps */}
              <div className="space-y-8 md:space-y-0">
                {[
                  {step: 1, title: "Kickoff Call", description: "Gather requirements and align on vision"},
                  {step: 2, title: "Creative Brief", description: "Document brand elements and design goals"},
                  {step: 3, title: "Design & Copywriting", description: "Create visual identity and compelling content"},
                  {step: 4, title: "SEO Setup", description: "Optimize for search visibility and performance"},
                  {step: 5, title: "Launch & Transfer", description: "Go live and provide handover training"}
                ].map((step, i) => (
                  <ScrollReveal key={i} delay={100 + (i * 100)} className="relative">
                    <div className="md:ml-16 md:pl-8 pb-8 relative flex md:items-center md:flex-row flex-col">
                      <div className="hidden md:flex absolute -left-8 items-center justify-center w-16 h-16 rounded-full bg-astrem-gray-100 border border-astrem-gray-200 text-astrem-black font-semibold text-lg">
                        {step.step}
                      </div>
                      <div className="md:hidden flex items-center mb-3">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-astrem-gray-100 border border-astrem-gray-200 text-astrem-black font-semibold text-lg mr-4">
                          {step.step}
                        </div>
                        <h3 className="text-xl font-semibold text-astrem-black">{step.title}</h3>
                      </div>
                      <div>
                        <h3 className="hidden md:block text-xl font-semibold text-astrem-black mb-2">{step.title}</h3>
                        <p className="text-astrem-gray-700">{step.description}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Tailored for Serious Businesses */}
      <section className="py-16 lg:py-24 bg-astrem-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-astrem-black mb-6 text-center">
              Tailored for Serious Businesses
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-700 max-w-3xl mx-auto text-center mb-12">
              Our websites are custom-built for businesses making moves—not tinkering with DIY tools. 
              If your company's doing $1M+ a year, you need a web presence that matches your level.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <ScrollReveal delay={200}>
              <Card className="border-0 shadow-md">
                <CardContent className="p-6 md:p-8">
                  <h3 className="text-xl font-semibold mb-4">Before Astrem</h3>
                  <ul className="space-y-3">
                    {[
                      "Outdated design that doesn't match your business quality",
                      "Slow load times frustrating potential customers",
                      "Constant plugin updates and maintenance",
                      "Security vulnerabilities putting your data at risk",
                      "Difficulty making simple content updates"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 mr-3 flex-shrink-0">
                          ✕
                        </span>
                        <span className="text-astrem-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="p-6 md:p-8">
                  <h3 className="text-xl font-semibold mb-4">After Astrem</h3>
                  <ul className="space-y-3">
                    {[
                      "Premium design that elevates your brand perception",
                      "Lightning-fast performance that converts visitors",
                      "Zero plugin management or WordPress headaches",
                      "Rock-solid security and reliable hosting",
                      "Simple content updates you can make yourself"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 mr-3 flex-shrink-0">
                          ✓
                        </span>
                        <span className="text-astrem-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Section 3: No More WordPress */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-astrem-black mb-6 text-center">
              No More WordPress. Seriously.
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-700 max-w-3xl mx-auto text-center mb-12">
              Still juggling plugin updates, slow load speeds, and confusing dashboards? 
              We build modern brochure-style websites on a platform that's stable, fast, and easy to manage.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-xl border border-astrem-gray-200">
                <div className="flex items-center mb-4">
                  <Plug className="h-8 w-8 text-red-500 mr-3" />
                  <h3 className="text-xl font-semibold">WordPress Headaches</h3>
                </div>
                <ul className="space-y-3 text-astrem-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Plugin conflicts breaking your site</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Security vulnerabilities requiring constant vigilance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Bloated code slowing down page loads</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Confusing dashboard with endless settings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Expensive developer fees for simple changes</span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="bg-white p-6 rounded-xl border border-astrem-gray-200 shadow-md">
                <div className="flex items-center mb-4">
                  <Code className="h-8 w-8 text-green-600 mr-3" />
                  <h3 className="text-xl font-semibold">Astrem Features</h3>
                </div>
                <ul className="space-y-3 text-astrem-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Streamlined architecture with zero extension bloat</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Advanced security with automatic protection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Modern tech stack for blazing-fast load times</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Visual editor that anyone can use</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Edit content in real-time with instant previews</span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Section 4: Real Businesses, Real Results */}
      <section className="py-16 lg:py-24 bg-astrem-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-astrem-black mb-6 text-center">
              Real Businesses. Real Results.
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-700 max-w-3xl mx-auto text-center mb-12">
              See how businesses like yours transformed their online presence with custom Astrem websites.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                image: "https://app.milanote.com/media/p/images/1TZl4J1uaXLgcv/VFq/combined_screenshots_with_drawing%20%288%29.jpg",
                business: "The Vladimir Collection",
                industry: "Luxury Real Estate",
                goal: "Luxury real estate website design"
              },
              {
                image: "https://app.milanote.com/media/p/images/1TZl4J1uaXLgcw/HWE/combined_screenshots_with_drawing%20%289%29.jpg",
                business: "Pinnacle Roofing",
                industry: "Construction",
                goal: "Full brand identity and website redesign"
              },
              {
                image: "https://app.milanote.com/media/p/images/1TZlHu1uaXLgcx/1pk/combined_screenshots_with_drawing%20%2810%29.jpg",
                business: "H.A. May Construction",
                industry: "Construction",
                goal: "Construction company brand refresh and website"
              }
            ].map((project, i) => (
              <ScrollReveal key={i} delay={200 + (i * 100)}>
                <div className="bg-white rounded-xl overflow-hidden border border-astrem-gray-200 shadow-md">
                  <div className="h-56 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.business} 
                      className="w-full h-full object-cover object-top" 
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-xl mb-1">{project.business}</h3>
                    <p className="text-astrem-gray-600 text-sm mb-2">{project.industry}</p>
                    <p className="text-astrem-gray-700">{project.goal}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-astrem-gray-900 to-astrem-black text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6">
                Let's Build Something Better.
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-white/90 mb-8">
                You've grown your business. Now it's time for your website to catch up. 
                Let's talk through what's possible—no pressure, no cookie-cutter templates.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="flex flex-col items-center justify-center mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 inline-block">
                  <ul className="inline-block text-left space-y-4">
                    {[
                      {icon: Check, text: "30-min discovery call"},
                      {icon: Check, text: "Tailored strategy"},
                      {icon: Check, text: "Beautiful results"}
                    ].map((item, i) => (
                      <li key={i} className="flex items-center">
                        <item.icon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                        <span className="text-white">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Button 
                size="lg" 
                className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full text-lg px-8"
                onClick={() => {
                  const element = document.getElementById('inline-embed-container') || 
                                  document.getElementById('inline-embed-container-mobile');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
                Book Your Free Discovery Call
                <ArrowRight className="ml-1" />
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default FacebookAdLanding;
